import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/localStorage';
import type { AiChatSurface } from './hooks/useAiChatController';

/**
 * How long a conversation stays "current" after its last activity. Opening a surface within the
 * window resumes it; past the window a fresh chat opens, with the previous one a click away.
 */
export const AI_CHAT_RESUME_WINDOW_MS = 5 * 60 * 1000;

export interface AiChatResumeRecord {
    threadId: number;
    /** Epoch millis of the last time the thread was current or received a message. */
    lastActiveAt: number;
}

const storageKey = (surface: AiChatSurface) => `ai-chat-resume-${surface}`;

export const readResumeRecord = (surface: AiChatSurface): AiChatResumeRecord | null => {
    const raw = getLocalStorage(storageKey(surface));
    if (!raw) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        const record = parsed as Partial<AiChatResumeRecord>;
        if (
            typeof record.threadId === 'number' &&
            Number.isSafeInteger(record.threadId) &&
            record.threadId > 0 &&
            typeof record.lastActiveAt === 'number' &&
            Number.isFinite(record.lastActiveAt)
        ) {
            return { threadId: record.threadId, lastActiveAt: record.lastActiveAt };
        }
    } catch {
        // Malformed entry: treat as absent rather than failing the open.
    }

    return null;
};

export const isResumeFresh = (record: AiChatResumeRecord | null): record is AiChatResumeRecord => {
    if (record == null) {
        return false;
    }

    // A negative gap means the clock moved back since the record was written; that record would
    // otherwise stay "fresh" until real time caught up with it.
    const idleMs = Date.now() - record.lastActiveAt;
    return idleMs >= 0 && idleMs <= AI_CHAT_RESUME_WINDOW_MS;
};

export const touchResumeRecord = (surface: AiChatSurface, threadId: number) => {
    setLocalStorage(storageKey(surface), JSON.stringify({ threadId, lastActiveAt: Date.now() }));
};

export const clearResumeRecord = (surface: AiChatSurface) => {
    removeLocalStorage(storageKey(surface));
};

/**
 * Drops both surfaces' records. The key is not scoped to a user, so the next person to log in on
 * this browser would otherwise inherit a window onto a conversation that is not theirs — the server
 * refuses it, but only after the surface has already tried to open it.
 */
export const clearAllResumeRecords = () => {
    clearResumeRecord('page');
    clearResumeRecord('floating');
};
